import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import Preloader from 'components/Preloader';
import { Modal } from 'ui-library';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    isAnyOfThreeBProductTypes,
} from 'hooks/isThreeBProduct';
import { useLayout } from 'pages/ClientOverview/pages/Portfolios/hooks/useLayout';
import { getPortfolioStatus } from 'constants/portfolioStatuses';
import EmptyContent from 'components/EmptyContent';
import {
    APPLICATION_CONTAINER_ID, EXTERNAL_PRODUCTS, PRODUCT_EXTERNAL_ID, QUALITY_CHECK_DATA, RISK_CATEGORIES_FOR_WITHDRAWAL, THREEBTYPES,
} from 'constants/constants';
import { CreatePortfolioManager } from 'datasource/CreatePortfolio';
import { PortfolioProcessLayoutManager } from 'datasource/PortfolioProcessLayout';
import { accessPolicies } from 'prodivers/managerProfile/constants';
import { useManagerProfile } from 'prodivers/managerProfile';
import arrayContains from 'utils/arrayUtils';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { StepperManager } from '../../../../../../../../core/stepper/StepperManager';
import { modifyPlan } from '../../../../../../utils/utils';
import { useSinglePortfolio } from '../../context';

import BasicStrategy from './BasicStrategy';
import IndividualStratagy from './IndividualStratagy';
import ChangeWithdrawalPlan from '../../../ChangeWithdrawalPlan';
import './Strategy.css';

const Strategy = () => {
    const navigate = useNavigate();
    const { clearLayoutCache } = useLayout();
    const { dfsClientId, portfolioId } = useParams();
    const { t, i18n: { language } } = useTranslation();
    const { hasAccessPolicy } = useManagerProfile();

    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);

        return () => {
            setIsMounted(false);
        };
    }, []);

    const {
        isPortfolioChangeable,
        portfolioProducts,
        strategyName,
        riskCategories,
        riskName,
        modelPortfolioDescription,
        getSelectStrategy,
        getFormattedXAxisFormat,
        allocations,
        isLoading,
        error,
        portfolioData,
        isIndividualPortfolio,
        ongoingPlan,
        individualPortfolioData,
        isRebalancing,
        withdrawalData,
        hasAccessToChangeStrategy,
        clientProfile,
    } = useSinglePortfolio() as any;

    const { sectionedPortfolios } = clientProfile || {};

    const total3aAmount = useMemo(() => sectionedPortfolios?.jointRegularPortfolios?.filter((item: any) => ([EXTERNAL_PRODUCTS.aaa, EXTERNAL_PRODUCTS.aaap]?.includes(item?.ProductExternalId?.toLowerCase())) && (item?.Status?.value === 'active'))?.reduce((accumulator: number, currentValue: any) => accumulator + currentValue.rawValue, 0), [sectionedPortfolios?.jointRegularPortfolios]);

    const {
        data: lastApplicationByPortfolio,
    } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getLastApplicationByPortfolio',
        payload: { portfolioId, language },
        enabled: !!portfolioId,
        staleTime: 10 * 60 * 1000,
        gcTime: 20 * 60 * 1000,
    });

    const investorProfileClientId = lastApplicationByPortfolio?.data?.investmentDescription?.investorProfileContactId;

    const modalContent = useMemo(() => (
        <div>
            <span>{t('strategy.blockChangePlan.modalContentDesc')}</span>
            <div>
                {t('strategy.blockChangePlan.modalContentCurrent', { plan: ongoingPlan?.current?.[language]?.['#text'] })}
            </div>
            <div>
                {t('strategy.blockChangePlan.modalContentSubmitted', { plan: ongoingPlan?.submitted?.[language]?.['#text'] })}
            </div>
        </div>
    ), [language, ongoingPlan]);

    useEffect(() => {
        if (ongoingPlan && !isLoading && ongoingPlan?.portfolioId === portfolioId && isMounted) {
            Modal.info({
                title: t('strategy.blockChangePlan.modalTitle'),
                content: modalContent,
            });
        }
    }, [ongoingPlan?.portfolioId, isLoading, modalContent, portfolioId, isMounted, language]);

    const isThreeBProduct = useMemo(() => isAnyOfThreeBProductTypes(portfolioData?.Product?.ExternalId), [portfolioData?.Product?.ExternalId]);
    const isZifdZiv = useMemo(() => arrayContains([THREEBTYPES.zivv, THREEBTYPES.zifd], portfolioData?.Product?.ExternalId), [portfolioData?.Product?.ExternalId]);
    const isZivv = useMemo(() => arrayContains([THREEBTYPES.zivv], portfolioData?.Product?.ExternalId), [portfolioData?.Product?.ExternalId]);
    const isAZP = useMemo(() => arrayContains([THREEBTYPES.azpNeu, THREEBTYPES.azpNeuSB, THREEBTYPES.azpAlt], portfolioData?.Product?.ExternalId), [portfolioData?.Product?.ExternalId]);

    const onChangePlan = useCallback(() => {
        sessionStorage.removeItem('totalAmountOfOtherAssets');
        if (!hasAccessToChangeStrategy) return;

        clearLayoutCache();
        sessionStorage.removeItem(StepperManager.LOCAL_STORAGE_KEY);
        sessionStorage.removeItem(CreatePortfolioManager.LOCAL_STORAGE_KEY);
        sessionStorage.removeItem(PortfolioProcessLayoutManager.LOCAL_STORAGE_KEY);
        sessionStorage.removeItem(APPLICATION_CONTAINER_ID);
        sessionStorage.removeItem(QUALITY_CHECK_DATA);
        if (isZivv || isAZP) {
            sessionStorage.removeItem(PRODUCT_EXTERNAL_ID);
            navigate(`/client/${dfsClientId}/portfolios/new-portfolio/`, {
                state: {
                    changePlan: true,
                    contactGroupId: portfolioData?.ContactGroupId,
                    investorProfileClientId,
                    portfolioId: portfolioData?.Id,
                },
            });
        } else {
            sessionStorage.setItem(PRODUCT_EXTERNAL_ID, portfolioData?.Product?.ExternalId);
            modifyPlan({
                path: `/client/${dfsClientId}/portfolios/${portfolioId}/change-strategy/${(isThreeBProduct && portfolioData?.Product?.ExternalId !== THREEBTYPES.zifd) ? 'advisory-setup' : 'personal-details'}`,
                action: 'ChangeStrategy',
                t,
                id: portfolioId as string,
                status: getPortfolioStatus({
                    portfolioStatusId: portfolioData?.PortfolioStatusId,
                }),
                productExternalId: portfolioData?.Product?.ExternalId,
                amount: portfolioData?.Product?.ExternalId === EXTERNAL_PRODUCTS.aaa ? total3aAmount : portfolioData?.CurrentValue,
                navigate,
            });
        }
    }, [
        dfsClientId,
        portfolioId,
        portfolioData?.PortfolioStatusId,
        portfolioData?.Product?.ExternalId,
        portfolioData?.IsBreached,
        portfolioData?.HasPendingOrders,
        portfolioData?.ContactGroupId,
        portfolioData?.CurrentValue,
        portfolioData?.Id,
        investorProfileClientId,
        isAZP, isZivv,
        hasAccessToChangeStrategy,
        total3aAmount,
        clearLayoutCache,
        navigate,
        isThreeBProduct,
        t,
    ]);

    const hasAccessToChangeWithdrawal = useMemo(() => hasAccessPolicy(accessPolicies?.EnableZIVVWithdrawalPlanChange) || hasAccessPolicy(accessPolicies?.EnableZIFDWithdrawalPlanChange), [hasAccessPolicy]);

    return (
        <Preloader isLoading={isLoading} error={error}>
            <EmptyContent data={allocations}>
                <div className="container">
                    <div className="row">
                        {isIndividualPortfolio ? (
                            <IndividualStratagy
                                isRebalancing={isRebalancing}
                                riskName={riskName}
                                individualPortfolioData={individualPortfolioData}
                                ongoingPlan={ongoingPlan}
                                onChangePlan={onChangePlan}
                                allocations={allocations}
                                strategyName={strategyName}
                                portfolioProducts={portfolioProducts}
                                t={t}
                                riskCategories={riskCategories}
                                strategy={getSelectStrategy()}
                                getFormattedXAxisFormat={getFormattedXAxisFormat}
                                isPortfolioChangeable={isPortfolioChangeable}
                                productExternalId={portfolioData?.Product?.ExternalId}
                                isAZP={isAZP}
                                isZivv={isZivv}
                                hasAccessToChangeStrategy={hasAccessToChangeStrategy}
                            />
                        ) : (
                            <BasicStrategy
                                riskName={riskName}
                                modelPortfolioDescription={modelPortfolioDescription}
                                ongoingPlan={ongoingPlan}
                                onChangePlan={onChangePlan}
                                allocations={allocations}
                                portfolioProducts={portfolioProducts}
                                t={t}
                                riskCategories={riskCategories}
                                strategy={getSelectStrategy()}
                                getFormattedXAxisFormat={getFormattedXAxisFormat}
                                isPortfolioChangeable={isPortfolioChangeable}
                                withdrawalData={withdrawalData}
                                isAZP={isAZP}
                                isZivv={isZivv}
                                hasAccessToChangeStrategy={hasAccessToChangeStrategy}
                            />
                        )}
                    </div>

                    {isZifdZiv && RISK_CATEGORIES_FOR_WITHDRAWAL?.includes(portfolioData?.RiskCategory?.Id) && (
                        <ChangeWithdrawalPlan
                            bankIBan={withdrawalData?.bankIban}
                            dfsClientId={dfsClientId as string}
                            portfolioId={portfolioId as string}
                            ongoingPlan={ongoingPlan}
                            hasAccessToChangeStrategy={hasAccessToChangeStrategy}
                            hasAccessToChangeWithdrawal={hasAccessToChangeWithdrawal}
                        />
                    )}
                </div>
            </EmptyContent>
        </Preloader>
    );
};

export default Strategy;
